/**
 * @file Components/Layouts/Footer.jsx
 * @description Site footer with company info, quick links, contact details,
 * social media icons, and copyright notice. All content driven by SiteSettings.
 */

import React from "react";
import { FaInstagram, FaWhatsapp, FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaTwitter, FaLinkedin, FaYoutube } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useSiteSettings } from "../../Context/SiteSettingsContext";

const NikabaFooter = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSiteSettings();
  const co = settings?.companyInfo || {};
  const social = settings?.socialLinks || {};
  const ft = settings?.footer || {};
  const addr = co.address || {};
  const fullAddress = [addr.street, addr.city, addr.state, addr.postalCode].filter(Boolean).join(', ');
  const copyrightText = (ft.copyrightText || '© {year} Nikaba Travels. All rights reserved.').replace('{year}', currentYear);

  return (
    <footer className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -left-20 -bottom-20 w-96 h-96 rounded-full bg-blue-500 blur-3xl"></div>
        <div className="absolute right-0 top-0 w-96 h-96 rounded-full bg-purple-500 blur-3xl"></div>
        <div className="absolute left-1/2 top-1/2 w-96 h-96 rounded-full bg-pink-500 blur-3xl"></div>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-16">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              {co.name || 'Nikaba Travels'}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {ft.aboutText || "Crafting unforgettable journeys across India's most beautiful destinations."}
            </p>
            <div className="flex space-x-4 pt-4">
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-blue-600 transition-all duration-300 transform hover:scale-110">
                  <FaFacebook className="text-lg" />
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-gradient-to-br hover:from-pink-500 hover:to-purple-600 transition-all duration-300 transform hover:scale-110">
                  <FaInstagram className="text-lg" />
                </a>
              )}
              {social.twitter && (
                <a href={social.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-blue-400 transition-all duration-300 transform hover:scale-110">
                  <FaTwitter className="text-lg" />
                </a>
              )}
              {social.linkedin && (
                <a href={social.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-blue-700 transition-all duration-300 transform hover:scale-110">
                  <FaLinkedin className="text-lg" />
                </a>
              )}
              {social.youtube && (
                <a href={social.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-red-600 transition-all duration-300 transform hover:scale-110">
                  <FaYoutube className="text-lg" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {(ft.quickLinks || []).map((link, i) => (
                <li key={i}><Link to={link.url} className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"><span className="w-0 group-hover:w-2 h-0.5 bg-blue-400 mr-0 group-hover:mr-2 transition-all duration-300"></span>{link.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Popular Destinations */}
          <div>
            <h4 className="text-lg font-bold mb-4">Popular Destinations</h4>
            <ul className="space-y-3">
              {(ft.popularDestinations || []).map((dest, i) => (
                <li key={i}><Link to={dest.url} className="text-gray-300 hover:text-white transition-colors duration-300">{dest.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4">Get In Touch</h4>
            <ul className="space-y-4">
              {co.phone && (
                <li>
                  <a href={`tel:${co.phone.replace(/\s/g, '')}`} className="flex items-start space-x-3 text-gray-300 hover:text-white transition-colors duration-300 group">
                    <FaPhone className="mt-1 text-blue-400 group-hover:scale-110 transition-transform" />
                    <span>{co.phone}</span>
                  </a>
                </li>
              )}
              {co.email && (
                <li>
                  <a href={`mailto:${co.email}`} className="flex items-start space-x-3 text-gray-300 hover:text-white transition-colors duration-300 group">
                    <FaEnvelope className="mt-1 text-blue-400 group-hover:scale-110 transition-transform" />
                    <span>{co.email}</span>
                  </a>
                </li>
              )}
              {fullAddress && (
                <li>
                  <div className="flex items-start space-x-3 text-gray-300">
                    <FaMapMarkerAlt className="mt-1 text-blue-400" />
                    <span>{fullAddress}</span>
                  </div>
                </li>
              )}
            </ul>
            
            {/* WhatsApp CTA */}
            {co.whatsapp && (
              <a 
                href={`https://wa.me/${co.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center space-x-2 bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 rounded-full font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-500/50"
              >
                <FaWhatsapp className="text-xl" />
                <span>Chat on WhatsApp</span>
              </a>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 my-8"></div>

        {/* Contact Strip */}
        <div className="mb-12">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <p className="text-center text-gray-300 mb-4 text-sm">
              Ready to start your journey? Get in touch with us today!
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/10">
          <div className="flex flex-wrap justify-center gap-6 mb-4 md:mb-0">
            {(ft.bottomLinks || []).map((link, i) => (
              <Link key={i} to={link.url} className="text-gray-400 hover:text-white transition-colors duration-300 text-sm">{link.label}</Link>
            ))}
          </div>
          <p className="text-gray-400 text-sm">{copyrightText}</p>
        </div>

        {/* Trust Badges */}
        {(ft.trustBadges || []).length > 0 && (
          <div className="mt-8 pt-8 border-t border-white/10">
            <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
              {ft.trustBadges.map((badge, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl font-bold text-white">{badge.value}</p>
                  <p className="text-xs text-gray-400">{badge.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};

export default NikabaFooter;
